import { useCallback, useEffect, useState } from 'react';
import { Seletor } from '../Filtros';
import {
  AlternarDaBarra, BarraDeLista, BuscaDaBarra, FiltrosAtivos, RodapeDaLista, usePaginacao,
} from '../BarraDeLista';
import { api } from '../api';
import { data } from '../formato';
import { Carregando, ErroDaApi, Selo, Vazio } from '../ui';
import type { Tom } from '../ui';

/**
 * Catálogo de normas — o que a `0032` carregou.
 *
 * 99 normas em 122 edições, lidas dos PDFs que a Testesolo forneceu. Sem
 * esta tela o catálogo existia só para quem abre o banco, que é
 * exatamente o descompasso que `modulos.ts` existe para não deixar
 * acontecer.
 *
 * A tela é **por edição**, não por norma. É a diferença que motivou a
 * migração inteira: "conforme a NBR 7182" não diz qual ensaio foi feito —
 * a 2016 e a 2025 divergem no texto e na referência da massa específica
 * dos sólidos. A coluna que importa é o ano.
 *
 * `vigente` aqui significa **a edição mais recente que o laboratório
 * tem**, não vigência nacional. A autoridade sobre isso é o catálogo da
 * ABNT e a `LM-GER-004` (`PD-60`), e a tela diz isso em vez de deixar o
 * selo verde sugerir mais do que ele sabe.
 *
 * Desde a `0176` a edição carrega uma SEGUNDA resposta, separada: o que
 * o mundo diz dela (`situacao`), com data e fonte da conferência. A
 * coluna «No mundo» mostra isso — e mostra «não conferida» quando é o
 * caso, que é a resposta honesta para a maior parte do catálogo. É a
 * fila do PD-60 à vista de quem pode fechá-la.
 */

interface Edicao {
  id: string;
  organismo: string;
  codigo: string;
  titulo: string;
  ano: number | null;
  edicao: string | null;
  versao_corrigida_em: string | null;
  vigente: boolean;
  arquivo_origem: string | null;
  citacao: string;
  erratas: number;
  metodos: number;
  situacao: 'vigente' | 'cancelada' | 'substituida' | 'em_revisao' | null;
  situacao_conferida_em: string | null;
  situacao_fonte: string | null;
  situacao_observacao: string | null;
  substituta: string | null;
}

const TOM = (e: Edicao): Tom => {
  if (e.edicao === 'cancelada') return 'critico';
  if (!e.vigente) return 'neutro';
  return 'ok';
};

const SITUACAO = (e: Edicao) => {
  if (e.edicao === 'cancelada') return 'Cancelada';
  if (e.edicao === 'substituída') return 'Substituída';
  if (!e.vigente) return 'Anterior';
  return 'Corrente';
};

/** O que o mundo diz da edição (0176). `null` é «ninguém conferiu». */
const NO_MUNDO: Record<NonNullable<Edicao['situacao']>, { rotulo: string; tom: Tom }> = {
  vigente:     { rotulo: 'Vigente',     tom: 'ok' },
  cancelada:   { rotulo: 'Cancelada',   tom: 'critico' },
  substituida: { rotulo: 'Substituída', tom: 'atencao' },
  em_revisao:  { rotulo: 'Em revisão',  tom: 'atencao' },
};

/** Os organismos do catálogo, para o filtro rápido. */
const ORGANISMOS = ['ABNT', 'ASTM', 'DNIT', 'ABGE'];

export function Normas() {
  const [linhas, setLinhas] = useState<Edicao[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [busca, setBusca] = useState('');
  const [organismo, setOrganismo] = useState<string | null>(null);
  const [soCorrentes, setSoCorrentes] = useState(true);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const q = new URLSearchParams();
      if (busca) q.set('busca', busca);
      if (organismo) q.set('organismo', organismo);
      if (soCorrentes) q.set('vigente', 'true');
      setLinhas(await api<Edicao[]>(`/normas/edicoes?${q}`));
    } catch (e) { setErro(e); }
  }, [busca, organismo, soCorrentes]);

  useEffect(() => {
    const t = setTimeout(() => { void carregar(); }, 250);
    return () => clearTimeout(t);
  }, [carregar]);
  const pag = usePaginacao(linhas ?? []);

  return (
    <div>
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Catálogo de normas</h1>
          <p className="sub">
            A edição, não a norma — é o ano que diz qual ensaio foi feito. Por
            organismo e código; a edição mais nova primeiro.
          </p>
        </div>
      </div>

      {/*
        * A barra do padrão (onda 273): o organismo era uma fileira de
        * botões sem estado acessível; virou o menu da barra, como em toda
        * lista. «Só as correntes» é a alternância com estado visível.
        */}
      <BarraDeLista rotulo="Filtros do catálogo de normas">
        <BuscaDaBarra valor={busca} aoMudar={setBusca}
          placeholder="Código ou título — NBR 7182, compactação, granulometria"
          rotulo="Buscar norma" />
        <Seletor naBarra rotulo="Organismo" valor={organismo} todos="Todos"
          opcoes={ORGANISMOS.map((o) => ({ valor: o, texto: o }))} aoMudar={setOrganismo} />
        <AlternarDaBarra ligado={soCorrentes} aoMudar={setSoCorrentes}>
          {soCorrentes ? '✓ Só as correntes' : 'Com as anteriores'}
        </AlternarDaBarra>
      </BarraDeLista>
      <FiltrosAtivos
        itens={[
          ...(busca ? [{ chave: 'q', texto: `Busca: ${busca}`, tirar: () => setBusca('') }] : []),
          ...(organismo ? [{ chave: 'org', texto: `Organismo: ${organismo}`, tirar: () => setOrganismo(null) }] : []),
        ]}
        aoLimpar={() => { setBusca(''); setOrganismo(null); }}
        contagem={linhas ? `${linhas.length} ${linhas.length === 1 ? 'edição' : 'edições'}${soCorrentes ? ' correntes' : ''}` : undefined} />

      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando />
        : (
          <div className="cartao">
            {linhas.length === 0 ? (
              <Vazio>Nenhuma edição com estes filtros. Use «Limpar filtros» ou «Com as anteriores».</Vazio>
            ) : (
              <div className="rola">
                <table className="dados">
                  <thead>
                    <tr>
                      <th scope="col">Norma</th>
                      <th scope="col" className="num">Ano</th>
                      <th scope="col" className="col-opcional">Arquivo do laboratório</th>
                      <th scope="col" className="col-opcional num">Métodos</th>
                      <th scope="col">No laboratório</th>
                      <th scope="col">No mundo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pag.visiveis.map((e) => (
                      <tr key={e.id} className={e.vigente ? undefined : 'apagada'}>
                        <th scope="row">
                          <strong>{e.citacao}</strong>
                          <span className="sub">{e.titulo}</span>
                        </th>
                        <td className="num">
                          {e.ano ?? '—'}
                          {e.versao_corrigida_em && (
                            <span className="sub">v. corrigida {data(e.versao_corrigida_em)}</span>
                          )}
                          {e.erratas > 0 && (
                            <span className="sub">{e.erratas} errata(s)</span>
                          )}
                        </td>
                        <td className="col-opcional">
                          <span className="sub">
                            {e.arquivo_origem ?? <span className="nada">sem PDF vinculado</span>}
                          </span>
                        </td>
                        <td className="col-opcional num">{e.metodos}</td>
                        <td><Selo tom={TOM(e)}>{SITUACAO(e)}</Selo></td>
                        <td>
                          {e.situacao ? (
                            <>
                              <Selo tom={NO_MUNDO[e.situacao].tom}>
                                {NO_MUNDO[e.situacao].rotulo}
                              </Selo>
                              <span className="sub" title={e.situacao_fonte ?? undefined}>
                                conferida em {data(e.situacao_conferida_em!)}
                              </span>
                              {e.substituta && (
                                <span className="sub">substituída pela {e.substituta}</span>
                              )}
                              {e.situacao_observacao && (
                                <span className="sub">{e.situacao_observacao}</span>
                              )}
                            </>
                          ) : (
                            <span className="nada">não conferida</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <RodapeDaLista {...pag} ordem="organismo, código, ano ↓" />

            <p className="rodape-regra">
              <strong>“Corrente” significa a edição mais recente que o
              laboratório tem</strong> — não vigência nacional. Vigência é a
              coluna <em>No mundo</em>: o que o catálogo da ABNT, o organismo
              ou a <code>LM-GER-004 Lista Mestra de Normas</code> dizem,
              conferido em data e fonte registradas. <em>Não conferida</em>
              é a resposta honesta enquanto ninguém conferir. A coluna
              <em>Métodos</em> conta quantos ensaios do sistema já apontam
              para esta edição; enquanto ela estiver em zero, o laudo cita
              norma por texto digitado no modelo.
            </p>
          </div>
        )}
    </div>
  );
}
